import AudioNotificationSrc from 'assets/audio/clear-announce-tone.wav'

import { Select } from 'components/Forms'
//import { LoadingBox } from 'components/States'
import { useMedia } from 'hooks/useMedia'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  FaExclamationCircle,
  FaMicrophoneAlt,
  FaPlay,
  FaRegDotCircle,
  /* FaTimesCircle,  */ FaVolumeUp,
} from 'react-icons/fa'
import { Button } from '@outbound-ai/ursa'
import styled, { useTheme } from 'styled-components'
import { ColorsEnum, ColorsToneEnum } from 'types/cssTypes'
import { notificator } from 'utils/notificator/notificator'
import storage, { StorageEnum } from 'utils/storage'
import WaveSurfer from 'wavesurfer.js'
import MicrophonePlugin from 'wavesurfer.js/dist/plugin/wavesurfer.microphone.min.js'
import CaretDownIcon from '../../../assets/images/icons/CaretDownIcon.svg'

export const SettingsAudioDevices = () => {
  // utils
  const theme = useTheme()
  const { microphonePermissionStatus, /* requestMicPermission,*/ audioInputDevices, audioOutputDevices } =
    useMedia(true)
  const speakerWavesurfer = useRef<null | WaveSurfer>(null)
  const micWavesurfer = useRef<null | WaveSurfer>(null)
  const micRecorder = useRef<null | MediaRecorder>(null)
  const micStream = useRef<null | MediaStream>(null)
  const recordingTimeInterval = useRef<null | NodeJS.Timer>(null)

  // local state
  const [audioInputDeviceId, setAudioInputDeviceId] = useState<string>(storage.get('microphoneDevideId') || 'default')
  const [audioOutputDeviceId, setAudioOutputDeviceId] = useState<string>('default')
  const [recordingTime, setRecordingTime] = useState(0)
  // const [isLoading, setIsLoading] = useState(true)

  // effects
  useEffect(() => {
    storage.set(StorageEnum.microphoneDevideId, audioInputDeviceId)
  }, [audioInputDeviceId])

  useEffect(() => {
    if (audioInputDevices?.length && !audioInputDevices.some((device) => device.value === audioInputDeviceId)) {
      setAudioInputDeviceId('default')
    }
  }, [audioInputDevices])

  useEffect(() => {
    recordingTime <= 0 && stopRecording()
  }, [recordingTime])

  useEffect(() => {
    return () => {
      recordingTimeInterval.current && clearInterval(recordingTimeInterval.current as any)
    }
  }, [])

  // Setup the wave display on initial render and when the speakers output is changed
  useEffect(() => {
    speakerWavesurfer.current?.destroy()
    speakerWavesurfer.current = WaveSurfer.create({
      ...commonWaversurferSettings,
      container: '#speaker-wave-display',
    })
    speakerWavesurfer.current?.load(AudioNotificationSrc)

    return () => speakerWavesurfer.current?.destroy()
  }, [audioOutputDeviceId])

  // Setup the wave display on initial render and when the microphone input is changed
  useEffect(() => {
    // setIsLoading(!microphonePermissionStatus)
    if (microphonePermissionStatus === 'granted') {
      micWavesurfer.current?.destroy()
      micWavesurfer.current = WaveSurfer.create({
        ...commonWaversurferSettings,
        cursorWidth: 0,
        container: '#mic-wave-display',
        plugins: [MicrophonePlugin.create({ constraints: { audio: { deviceId: audioInputDeviceId } } })],
      })
      micWavesurfer.current?.microphone.start()
    }

    return () => micWavesurfer.current?.destroy()
  }, [microphonePermissionStatus, audioInputDeviceId])

  // handlers
  const onAudioInputChange = async (e: React.FormEvent<HTMLSelectElement>) => {
    const { value } = e.currentTarget
    const deviceLabel = audioInputDevices.find((device) => device.value === value)?.label || 'Device not found'
    notificator.notify({
      heading: `"${deviceLabel}" will be used as your default microphone when starting new calls`,
      type: 'info',
    })
    //notificator.info(`"${deviceLabel}" will be used as your default microphone when starting new calls`)
    setAudioInputDeviceId(value)
  }

  const onAudioOutputChange = async (e: React.FormEvent<HTMLSelectElement>) => {
    const { value } = e.currentTarget
    setAudioOutputDeviceId(value)
  }

  const onTestSpeakers = async () => {
    await speakerWavesurfer.current?.setSinkId(audioOutputDeviceId)
    const audioCtx = speakerWavesurfer.current?.backend.getAudioContext()
    if (audioCtx?.state === 'suspended') {
      audioCtx.resume()
    }
    speakerWavesurfer.current?.isPlaying() ? speakerWavesurfer.current?.stop() : speakerWavesurfer.current?.play()
  }

  const stopRecording = () => {
    micRecorder.current?.stop()

    micStream.current?.getAudioTracks()[0].stop()
    recordingTimeInterval.current && clearInterval(recordingTimeInterval.current as any)
  }

  const onToggleRecording = () => {
    const audioCtx = micWavesurfer.current?.backend.getAudioContext()
    if (audioCtx?.state === 'suspended') {
      audioCtx.resume()
    }

    const container = document.getElementById('recording-preview') as HTMLDivElement
    container.replaceChildren()

    if (micRecorder.current?.state === 'recording') {
      setRecordingTime(0)
    } else {
      setRecordingTime(10)
      recordingTimeInterval.current = setInterval(() => {
        setRecordingTime((c) => c - 1)
      }, 1000)
      navigator.mediaDevices
        .getUserMedia({
          audio: { deviceId: audioInputDeviceId },
        })
        .then((stream) => {
          micStream.current = stream
          micRecorder.current = new MediaRecorder(stream)
          micRecorder.current.ondataavailable = function (e) {
            const url = URL.createObjectURL(e.data)
            const preview = document.createElement('audio')
            preview.controls = true
            preview.src = url
            container.appendChild(preview)
          }
          micRecorder.current.start()
        })
    }
  }

  // variables
  const micNotSetMessage = useMemo(() => {
    return microphonePermissionStatus === 'denied'
      ? 'We are not able to access your microphone due to a system setting. Please go to your browser settings and enable microphone access for Outbound.ai. This will allow you to place calls using the Console.'
      : 'You need to give us permission to access your microphone in order to be able to use use your headset during calls.'
  }, [microphonePermissionStatus])

  const commonWaversurferSettings = useMemo(() => {
    return {
      waveColor: theme.colors.info.light,
      progressColor: theme.colors.info.dark,
      closeAudioContext: true,
      cursorColor: theme.colors.info.dark,
      cursorWidth: 4,
      responsive: true,
    }
  }, [])

  return (
    <Div>
      {/*   {isLoading && <LoadingBox />} */}

      <div className="audio-devices-options">
        <div className="audio-devices-option">
          {microphonePermissionStatus === 'granted' ? (
            <>
              <header>
                <FaMicrophoneAlt size={30} color={theme.colors.primary.dark} />
              </header>

              <div className="d-flex align-items-end gap-3">
                <Select
                  options={audioInputDevices}
                  label="Microphone"
                  onChange={onAudioInputChange}
                  value={audioInputDeviceId}
                  readOnly={!!recordingTime}
                  disabled={!!recordingTime}
                />
                <div className="d-flex align-items-end flex-1">
                  <Button variant="info" size="small" radius="rounded" onClick={onToggleRecording}>
                    {recordingTime ? (
                      `Stop (${recordingTime})`
                    ) : (
                      <>
                        <FaRegDotCircle />
                        Test
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div>
                <div className="border" id="mic-wave-display" />
                <div className="recording-preview" id="recording-preview" />
              </div>
            </>
          ) : (
            <>
              <h3 className="d-flex align-items-center gap-3 pe-5">
                <div>
                  {microphonePermissionStatus === 'denied' ? (
                    <FaExclamationCircle size={50} color={theme.colors.error.dark} />
                  ) : (
                    <FaExclamationCircle size={50} color={theme.colors.error.dark} />
                  )}
                </div>
                {micNotSetMessage}
              </h3>

              {/*   <Button variant="info" size="large" width="100%" radius="rounded" onClick={requestMicPermission}>
                Grant microphone permission
              </Button> */}
            </>
          )}
        </div>

        <div className="audio-devices-option">
          <header>
            <FaVolumeUp size={30} color={theme.colors.primary.dark} />
          </header>
          <div className="d-flex align-items-end gap-3">
            <Select
              options={audioOutputDevices}
              label="Speaker"
              onChange={onAudioOutputChange}
              value={audioOutputDeviceId}
            />
            <Button variant="info" size="small" radius="rounded" onClick={onTestSpeakers}>
              <FaPlay size={10} />
              Test
            </Button>
          </div>
          <div>
            <div className="border" id="speaker-wave-display"></div>
          </div>
        </div>
      </div>
    </Div>
  )
}

const Div = styled.div`
  .audio-devices-options {
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: 2.5rem;

    > * {
      flex: 1;
      display: flex;
      align-items: stretch;
      flex-direction: column;
      gap: 1.5rem;
    }

    .audio-devices-option {
      border: 1px solid ${({ theme }) => theme.colors.primary.lightest};
      padding: 2rem 2.25rem;
      border-radius: ${({ theme }) => theme.borderRadius.rounded};
      header {
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: ${({ theme }) => `${theme.colors.primary.lightest}50`};
        border-radius: ${({ theme }) => theme.borderRadius.rounded};
        padding-block: 0.75rem;
      }
      select {
        padding: 0.5rem;
        padding-right: 2rem;
        -moz-appearance: none;
        -webkit-appearance: none;
        appearance: none;
        background-repeat: no-repeat;
        background-image: url(${CaretDownIcon});
        background-position: 95% center;
        background-size: 0.75rem;
      }

      .labelText {
        font-size: 1rem;
        color: ${({ theme }) => theme.colors.grey.dark};
      }
      #mic-wave-display,
      #speaker-wave-display {
        wave {
          height: 4rem !important;
          overflow: hidden !important;
        }
      }
      /* button {
        height: ${({ theme }) => theme.util.pixelToRem(39)};
      } */
    }

    font-weight: 500;
    ${({ theme }) => theme.typography.xs.normal};
    color: ${({ theme }) => theme.colors.grey.dark};

    select {
      background: ${({ theme }) => theme.colors.grey.lightest};
      outline: none;
      border: ${({ theme }) => theme.borders.s.solid(ColorsEnum.grey, ColorsToneEnum.light)};
    }

    .recording-preview {
      audio {
        margin-top: 1rem;
        width: 100%;
      }
    }

    /* .test-button {
      position: relative;
      bottom: 1px;
      padding: 0.5rem 1rem;
    } */
  }
`
